import { FC, memo } from 'react';
import { NodeProps, Position, useNodeConnections } from '@xyflow/react';
import styled from 'styled-components';
import {
  colorInactiveBg,
  fontSize,
  fontWeightNormal,
  letterSpacing,
} from '@atlas-frontend/design-tokens';
import {
  AddRemoveButton,
  TextEllipsis,
  Tooltip,
} from '../../../../common/components';
import { DataObjectDescriptionVersionStore } from '../../../DataObjectDescriptionVersionStore';
import { AlgorithmGraphReadOnlyField } from '../../AlgorithmGraphReadOnlyField';
import { GraphCard } from '../../GraphCard';
import { GraphHandle } from '../../GraphHandle';
import { AlgorithmTargetItemDimensionIndicatorSelect } from '../AlgorithmTargetItemDimensionIndicatorSelect';
import { AlgorithmTargetMappingTargetOptionLabel } from '../AlgorithmTargetMappingTargetOptionLabel';
import { useAlgorithmTargetRuleOptions } from '../ruleOptions/useAlgorithmTargetRuleOptions';
import { decodeRuleTargetItemValue } from '../utils';
import { AlgorithmTargetMappingGraphStore } from './AlgorithmTargetMappingGraphStore';
import { AlgorithmTargetMappingNode } from './types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmTargetMappingsGraphTargetNode.translations.json';
import { Tag } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-target-mappings-graph-target-node',
);
addTranslations(translations);

export const AlgorithmTargetMappingsGraphTargetNode: FC<
  NodeProps<AlgorithmTargetMappingNode>
> = (props) => {
  const [readOnly] = AlgorithmTargetMappingGraphStore.useValue('readOnly');
  const connections = useNodeConnections({ handleType: 'target' });

  return (
    <GraphCard size="small">
      <GraphHandle
        type="target"
        position={Position.Left}
        isConnectable={!connections.length}
      />
      {readOnly ? <ReadOnly {...props} /> : <Editable {...props} />}
    </GraphCard>
  );
};

const ReadOnly: FC<NodeProps<AlgorithmTargetMappingNode>> = memo((props) => {
  const { targetValue } = props.data;
  const { t } = useTranslation();
  const [isLoading] = AlgorithmTargetMappingGraphStore.useValue('isLoading');
  const [{ dimensions, indicators }] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const values = decodeRuleTargetItemValue(targetValue).map((v) => {
    const entity =
      'dimensionCode' in v
        ? dimensions?.find((d) => d.code === v.dimensionCode)
        : indicators?.find((i) => i.code === v.indicatorCode);
    return (
      <AlgorithmTargetMappingTargetOptionLabel
        {...entity!}
        confidentialityStatus={
          'confidentialityStatus' in v ? v.confidentialityStatus : undefined
        }
      />
    );
  });

  return (
    <AlgorithmGraphReadOnlyField label={t('label')} isLoading={isLoading}>
      <TextEllipsis>
        {values?.map((value, index) => (
          <StyledTag key={index} bordered={false}>
            {value}
          </StyledTag>
        ))}
      </TextEllipsis>
    </AlgorithmGraphReadOnlyField>
  );
});

const StyledTag = styled(Tag)`
  &.ant-tag {
    font-size: ${fontSize}px;
    font-weight: ${fontWeightNormal};
    letter-spacing: ${letterSpacing}em;
    background-color: ${colorInactiveBg};
  }
`;

const Editable: FC<NodeProps<AlgorithmTargetMappingNode>> = (props) => {
  const { index, sourceValue, targetValue } = props.data;
  const { t } = useTranslation();
  const [dataObjectVersion] = DataObjectDescriptionVersionStore.useValue(
    'dataObjectDescriptionVersion',
  );
  const [algorithmTypeCode] =
    AlgorithmTargetMappingGraphStore.useValue('algorithmTypeCode');
  const [sources] = AlgorithmTargetMappingGraphStore.useValue('sources');
  const [mappings] = AlgorithmTargetMappingGraphStore.useValue('mappings');
  const [handleRemove] =
    AlgorithmTargetMappingGraphStore.useValue('handleRemove');
  const { targetOptions } = useAlgorithmTargetRuleOptions({
    algorithmTypeCode,
    dataObjectVersion,
    sources,
    mappings,
    currentSourceValue: sourceValue,
    currentTargetValue: targetValue,
  });

  return (
    <>
      <AlgorithmTargetItemDimensionIndicatorSelect
        // `key` is needed for react-hook-form internal state to sync properly
        key={index}
        index={index}
        options={targetOptions}
      />
      <Tooltip title={t('remove')}>
        <StyledAddRemoveButton
          id="remove-mapping"
          type="remove"
          size="small"
          onClick={() => handleRemove?.(index)}
          className="nodrag nopan"
        />
      </Tooltip>
    </>
  );
};

const StyledAddRemoveButton = styled(AddRemoveButton)`
  position: absolute;
  right: 0;
  top: 0;
  transform: translate(50%, -50%);
`;
